import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { SidebarProvider, useSidebar } from "@/context/SidebarContext";
import { ShellHeaderProvider, useShellHeader } from "@/context/ShellHeaderContext";
import Sidebar from "@/components/Sidebar";
import BrandLogo from "@/components/BrandLogo";
import { Toaster } from "sonner";
import LandingPage from "@/pages/LandingPage";
import DemoPage from "@/pages/DemoPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import OnboardingPage from "@/pages/OnboardingPage";
import ChatPage from "@/pages/ChatPage";
import CasePage from "@/pages/CasePage";
import DocumentsPage from "@/pages/DocumentsPage";
import DocumentEditor from "@/pages/DocumentEditor";
import TemplatesPage from "@/pages/TemplatesPage";
import ResearchAgentPage from "@/pages/ResearchAgentPage";
import WarRoomPage from "@/pages/WarRoomPage";
import MatterDashboard from "@/pages/MatterDashboard";
import JollyPage from "@/pages/JollyPage";
import AinaPage from "@/pages/AinaPage";
import KnowledgePage from "@/pages/KnowledgePage";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import DailyBriefPage from "@/pages/DailyBriefPage";
import TodayPage from "@/pages/TodayPage";
import BoardPage from "@/pages/BoardPage";
import AuthoritiesPage from "@/pages/AuthoritiesPage";
import DraftingHubPage from "@/pages/DraftingHubPage";
import HearingsPage from "@/pages/HearingsPage";
import TeamOpsPage from "@/pages/TeamOpsPage";
import ClientLoopPage from "@/pages/ClientLoopPage";
import StudentModePage from "@/pages/StudentModePage";
import SavedPage from "@/pages/SavedPage";
import SettingsPage from "@/pages/SettingsPage";
import SharePage from "@/pages/SharePage";
import AdminPage from "@/pages/AdminPage";
import EarlyBirdPopup from "@/components/EarlyBirdPopup";
import SentinelOverlay from "@/components/SentinelOverlay";
import WorkspaceSwitcher from "@/components/layout/WorkspaceSwitcher";
import { LogOut, Menu, Moon, Plus, Sun, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import {
  AUTHENTICATED_HOME_PATH,
  DRAFTS_HOME_PATH,
  MATTER_HOME_PATH,
  SEARCH_HOME_PATH,
  SETTINGS_HOME_PATH,
  TEAM_HOME_PATH,
  resolveShellLabel,
} from "@/lib/sidebarNav";
import "@/App.css";

function AppShellHeader({ onLogout }) {
  const {
    user,
    availableWorkspaces,
    activeWorkspace,
    setActiveWorkspace,
  } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { collapsed, toggle, isMobile, toggleSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const shellHeader = useShellHeader();
  const shellLabel = resolveShellLabel(location.pathname);
  const title = shellHeader?.headerMeta?.title || shellLabel;
  const eyebrow = shellHeader?.headerMeta?.eyebrow || (activeWorkspace?.type === "team" ? "Team workspace" : "Personal workspace");

  const handleWorkspaceSelect = (workspace) => {
    if (!workspace || workspace.key === activeWorkspace?.key) return;
    setActiveWorkspace(workspace);
    navigate(workspace.type === "team" ? TEAM_HOME_PATH : AUTHENTICATED_HOME_PATH);
  };

  return (
    <header className="app-shell-header">
      <div className="app-shell-header__brand-group">
        <button
          type="button"
          onClick={isMobile ? toggleSidebar : toggle}
          aria-label={isMobile ? "Toggle sidebar" : collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="sidebar-mobile-toggle app-shell-header__menu"
        >
          {isMobile ? <Menu size={18} /> : collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>

        <div className="app-shell-header__brand-badge">
          <BrandLogo variant="full" className="app-shell-header__logo" />
        </div>

        <div className="app-shell-header__copy">
          <span className="app-shell-header__eyebrow">{eyebrow}</span>
          <span className="app-shell-header__title">{title}</span>
        </div>
      </div>

      <div className="app-shell-header__actions-wrap">
        <WorkspaceSwitcher
          workspaces={availableWorkspaces}
          activeWorkspace={activeWorkspace}
          onSelect={handleWorkspaceSelect}
        />

        <div ref={shellHeader?.registerActionSlot || null} className="app-shell-header__page-actions" />

        <button type="button" onClick={() => navigate("/chat")} className="advoq-btn-primary app-shell-header__primary-action">
          <Plus size={15} />
          New Chat
        </button>

        <button
          type="button"
          onClick={toggleTheme}
          className="app-sidebar__icon-button app-shell-header__icon-button"
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
        </button>

        <div className="app-shell-header__user-chip">
          <span className="app-shell-header__avatar">{user?.name?.[0]?.toUpperCase() || "U"}</span>
          <span className="app-shell-header__user-name">{user?.name || "User"}</span>
        </div>

        <button
          type="button"
          onClick={onLogout}
          className="app-sidebar__icon-button app-shell-header__icon-button"
          title="Logout"
        >
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}

function AppShellFrame({ children }) {
  const {
    collapsed,
    isMobile,
    mobileOpen,
    closeMobileSidebar,
  } = useSidebar();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="app-layout">
      <Sidebar
        collapsed={collapsed}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onMobileClose={closeMobileSidebar}
      />
      <div className="app-shell__body">
        <AppShellHeader onLogout={handleLogout} />
        <div className="app-shell__content">
          {children}
        </div>
      </div>
    </div>
  );
}

function AppShell({ children }) {
  return (
    <ShellHeaderProvider>
      <AppShellFrame>{children}</AppShellFrame>
    </ShellHeaderProvider>
  );
}

function ProtectedRoute({ children, shell = true }) {
  const { user, loading } = useAuth();
  const { theme } = useTheme();

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
          <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;
  if (!user.onboarding_done && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return (
    <div className={`${theme}-theme`} style={{ display: 'contents' }}>
      {shell ? <AppShell>{children}</AppShell> : children}
    </div>
  );
}

export function TeamWorkspaceRoute({ children }) {
  const {
    activeWorkspace,
    defaultTeamWorkspace,
    hasTeamWorkspace,
    setActiveWorkspace,
  } = useAuth();

  useEffect(() => {
    if (!hasTeamWorkspace || !defaultTeamWorkspace) return;
    if (activeWorkspace?.type === 'team') return;
    setActiveWorkspace(defaultTeamWorkspace);
  }, [activeWorkspace?.type, defaultTeamWorkspace, hasTeamWorkspace, setActiveWorkspace]);

  if (!hasTeamWorkspace) {
    return <Navigate to={AUTHENTICATED_HOME_PATH} replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  // Listen for auto-save events dispatched by page components on navigation away
  useEffect(() => {
    const handler = (e) => {
      const { title, location: draftLocation } = e.detail;
      const shortTitle = title.length > 50 ? title.slice(0, 50) + '…' : title;
      toast.success('Draft saved automatically', {
        description: `"${shortTitle}" saved to ${draftLocation}`,
        duration: 6000,
      });
    };
    window.addEventListener('draft-auto-saved', handler);
    return () => window.removeEventListener('draft-auto-saved', handler);
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/demo/*" element={<DemoPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/share/:token" element={<SharePage />} />
      <Route path="/onboarding" element={<ProtectedRoute shell={false}><OnboardingPage /></ProtectedRoute>} />
      <Route path="/today" element={<ProtectedRoute shell={false}><Navigate to={AUTHENTICATED_HOME_PATH} replace /></ProtectedRoute>} />
      <Route path="/board" element={<ProtectedRoute><BoardPage /></ProtectedRoute>} />
      <Route path="/authorities" element={<ProtectedRoute><AuthoritiesPage /></ProtectedRoute>} />
      <Route path="/drafting" element={<ProtectedRoute><DraftingHubPage /></ProtectedRoute>} />
      <Route path="/hearings" element={<ProtectedRoute><HearingsPage /></ProtectedRoute>} />
      <Route path="/voice-notes" element={<ProtectedRoute><JollyPage /></ProtectedRoute>} />
      <Route path="/red-team" element={<ProtectedRoute><AinaPage /></ProtectedRoute>} />
      <Route path="/red-team/:matterId" element={<ProtectedRoute><AinaPage /></ProtectedRoute>} />
      <Route path="/team-ops" element={<ProtectedRoute><TeamWorkspaceRoute><TeamOpsPage /></TeamWorkspaceRoute></ProtectedRoute>} />
      <Route path="/client-loop" element={<ProtectedRoute><TeamWorkspaceRoute><ClientLoopPage /></TeamWorkspaceRoute></ProtectedRoute>} />
      <Route path="/student-mode" element={<ProtectedRoute><StudentModePage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/chat/:chatId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/case" element={<ProtectedRoute><CasePage /></ProtectedRoute>} />
      <Route path="/case/:matterId" element={<ProtectedRoute><CasePage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<Navigate to={AUTHENTICATED_HOME_PATH} replace />} />
      <Route path={MATTER_HOME_PATH} element={<ProtectedRoute><TodayPage /></ProtectedRoute>} />
      <Route path="/matters/dashboard" element={<ProtectedRoute><MatterDashboard /></ProtectedRoute>} />
      <Route path="/matters/:matterId" element={<ProtectedRoute><WarRoomPage /></ProtectedRoute>} />
      <Route path={DRAFTS_HOME_PATH} element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
      <Route path="/documents/:docId" element={<ProtectedRoute><DocumentEditor /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      <Route path={SEARCH_HOME_PATH} element={<ProtectedRoute><ResearchAgentPage /></ProtectedRoute>} />
      <Route path="/research" element={<ProtectedRoute><ResearchAgentPage /></ProtectedRoute>} />
      <Route path="/research/agent" element={<ProtectedRoute><ResearchAgentPage /></ProtectedRoute>} />
      <Route path="/war-room" element={<ProtectedRoute><WarRoomPage /></ProtectedRoute>} />
      <Route path="/war-room/:matterId" element={<ProtectedRoute><WarRoomPage /></ProtectedRoute>} />
      <Route path="/jolly" element={<ProtectedRoute><JollyPage /></ProtectedRoute>} />
      <Route path="/aina" element={<ProtectedRoute><AinaPage /></ProtectedRoute>} />
      <Route path="/aina/:matterId" element={<ProtectedRoute><AinaPage /></ProtectedRoute>} />
      <Route path="/knowledge" element={<ProtectedRoute><KnowledgePage /></ProtectedRoute>} />
      <Route path="/knowledge-base" element={<ProtectedRoute><KnowledgeBasePage /></ProtectedRoute>} />
      <Route path="/brief" element={<ProtectedRoute shell={false}><Navigate to={AUTHENTICATED_HOME_PATH} replace /></ProtectedRoute>} />
      <Route path="/daily-brief" element={<ProtectedRoute><DailyBriefPage /></ProtectedRoute>} />
      <Route path="/saved" element={<ProtectedRoute><SavedPage /></ProtectedRoute>} />
      <Route path={TEAM_HOME_PATH} element={<ProtectedRoute><TeamWorkspaceRoute><TeamOpsPage /></TeamWorkspaceRoute></ProtectedRoute>} />
      <Route path={SETTINGS_HOME_PATH} element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function HomeRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LandingPage />;
  }

  if (!user) {
    return <LandingPage />;
  }

  if (!user.onboarding_done) {
    return <Navigate to="/onboarding" replace />;
  }

  return <Navigate to={AUTHENTICATED_HOME_PATH} replace />;
}

function GlobalNotifications() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    if (user.pending_notification === 'admin_promoted') {
      toast.success("You've been granted admin access on Advoq.", { duration: 8000 });
    }
  }, [user?.pending_notification]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <EarlyBirdPopup />
      <SentinelOverlay />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
      <SidebarProvider>
        <Toaster
          position="top-right"
          closeButton
          duration={4000}
          toastOptions={{
            style: {
              background: 'var(--bg-elevated)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-subtle)',
              fontFamily: 'var(--font-ui)',
            },
          }}
        />
        <AppRouter />
        <GlobalNotifications />
      </SidebarProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
